<script setup lang="ts">
import { type Component, computed, nextTick, ref, useTemplateRef } from 'vue';
import { ErrorUtil } from '@/modules/common/utils/error.util.ts';
import type { Card } from '@/modules/common/models/card/card';
import type { CardGetter, QueryOptionsGetter } from '@/modules/common/types/ui.ts';
import type { IconComponent } from '@/modules/common/types/icon.type.ts';
import type { Option } from '@/modules/common/types/option.type.ts';
import { useAbort } from '@/modules/common/composables/use-abort.ts';
import UiSearch from '@/modules/ui/components/ui/UiSearch.vue';
import { uniqueId } from 'lodash-es';
import { useI18n } from 'vue-i18n';
import { IdPrefix } from '@/modules/common/enums/id-prefix.ts';
import { notification } from '@/modules/common/composables/use-notification.ts';
import UiButton from '@/modules/ui/components/ui/UiButton.vue';
import UiCardList from '@/modules/ui/components/ui/UiCardList.vue';
import UiLoader from '@/modules/ui/components/ui/UiLoader.vue';

const model = defineModel<Card[]>({ required: true, default: () => [] });

const {
  id = uniqueId(IdPrefix.Control),
  label,
  placeholder = 'ui.searchMultiple.selectValue',
  addText = 'shared.add',
  getOptions,
  getCard,
  max,
  iconSpaced,
  addOption,
  addOptionLabel,
  onAddClick,
} = defineProps<{
  id?: string;
  label?: string;
  error?: boolean;
  placeholder?: string;
  addIcon: IconComponent;
  addText?: string;
  getOptions: QueryOptionsGetter<string | number>;
  getCard: CardGetter<string | number>;
  scroll?: boolean;
  cardFooter?: Component;
  max?: number;
  iconSpaced?: boolean;
  addOption?: (query: string) => void | Promise<void>;
  addOptionLabel?: string;
  onAddClick?: () => void;
}>();

const canAdd = computed(() => max == null || model.value.length < max);

const { signal } = useAbort();

const { maybeT } = useI18n();

const searchRef = useTemplateRef('uiSearch');

const searchModel = ref<Option | null>(null);
const isLoading = ref(false);

async function getNewOptions(query: string, signal: AbortSignal) {
  const options = await getOptions(query, signal);
  return options.filter((option) => model.value.every((current) => current.id !== option.code));
}

async function search(option: Option | null) {
  if (!option || !canAdd.value) {
    return;
  }

  void nextTick(() => (searchModel.value = null));
  isLoading.value = true;

  try {
    const item = await getCard(option.code, signal.value);
    model.value = [...model.value, item];
    isLoading.value = false;
  } catch (error) {
    if (!ErrorUtil.isUnexpected(error)) {
      return;
    }

    notification.error(
      { key: 'ui.notification.error.field.getOption', params: { field: maybeT(label || placeholder) } },
      error
    );

    isLoading.value = false;
  }
}

function onChange({ item, changes }: { item: { id: string | number }; changes: unknown }) {
  model.value = model.value.map((current) =>
    current.id === item.id ? { ...current, ...(changes as Partial<Card>) } : current
  );
}

function onRemove(id: number | string) {
  model.value = model.value.filter((current) => current.id !== id);
}
</script>

<template>
  <div class="ui-search-multiple">
    <UiSearch
      v-if="canAdd"
      :id
      ref="uiSearch"
      v-model="searchModel"
      :placeholder="placeholder"
      :get-options="getNewOptions"
      :add-option="addOption"
      :add-option-label="addOptionLabel"
      @update:model-value="search"
    />

    <div
      class="wrapper"
      :class="{ _scroll: scroll }"
    >
      <UiCardList
        v-if="model.length"
        :list="model"
        title-key="title"
        :card-footer="cardFooter"
        :icon-spaced="iconSpaced"
        @change="onChange"
        @remove="onRemove"
      />

      <UiButton
        v-else
        color="black"
        mode="outline"
        @click="onAddClick ? onAddClick() : searchRef?.focus()"
      >
        <component
          :is="addIcon"
          v-prefix
        />
        <span>{{ $t(addText) }}</span>
      </UiButton>

      <UiLoader v-if="isLoading" />
    </div>
  </div>
</template>

<style scoped lang="scss">
.ui-search-multiple {
  display: flex;
  flex-direction: column;
}

.ui-search {
  margin-bottom: 8px;
}

.wrapper {
  position: relative;
  display: flex;
  flex-grow: 1;
  min-height: 120px;
  justify-content: center;
  border: var(--border-dashed-gray-400);
  border-radius: 8px;

  &._scroll {
    overflow-y: auto;
  }
}

.ui-card-list {
  overflow-y: auto;
  max-height: 60vh;
}

.ui-button {
  margin: auto;
  align-self: center;
}

.ui-loader {
  border-radius: 8px;
}
</style>
